import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Divider, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ValidationSummary component shows validation results
 */
const ValidationSummary = ({ report }) => {
  const { t } = useTranslation();
  const { data, status } = report;
  const validationResult = data?.validation_result;
  const validationReason = data?.validation_reason;
  const lastCheckinTime = data?.last_checkin_time;
  const lastValidation = data?.last_validation;

  const isPassed = validationResult === 'passed' || status === 'passed';
  const isFailed = validationResult === 'failed' || status === 'failed';

  const getValidationReasonText = () => {
    switch (validationReason) {
      case 'no_checkin_within_24h':
        return 'Device has not checked in within 24 hours';
      case 'alarms_during_schedule_windows':
        return 'Alarms occurred during scheduled operation';
      default:
        return validationReason || 'Unknown reason';
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('reports.validationSummary', 'Validation Summary')}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {isPassed && <CheckCircleIcon color="success" fontSize="large" />}
        {isFailed && <ErrorIcon color="error" fontSize="large" />}
        <Box>
          <Typography variant="body1" fontWeight="bold">
            {isPassed ? 'Validation Passed' : 'Validation Failed'}
          </Typography>
          {isFailed && validationReason && (
            <Typography variant="body2" color="text.secondary">
              {getValidationReasonText()}
            </Typography>
          )}
        </Box>
      </Box>

      {lastCheckinTime && (
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('reports.lastCheckin', 'Last Check-in:')}</strong>{' '}
            {formatDistanceToNow(new Date(lastCheckinTime))} ago
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(lastCheckinTime).toLocaleString()}
          </Typography>
        </Box>
      )}

      {lastValidation && status === 'pending' && (
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('reports.lastValidation', 'Last Validation:')}</strong>{' '}
            {formatDistanceToNow(new Date(lastValidation))} ago
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(lastValidation).toLocaleString()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ValidationSummary.propTypes = {
  report: PropTypes.shape({
    status: PropTypes.string,
    data: PropTypes.shape({
      validation_result: PropTypes.string,
      validation_reason: PropTypes.string,
      last_checkin_time: PropTypes.string,
      last_validation: PropTypes.string,
    }),
  }).isRequired,
};

export default ValidationSummary;
